import './styles/main.css';

import {
  WORKLETS_BLOCKED,
  WORKLET_HINT,
  ensureGraph,
  setEchoWet,
  setFilterFrequency,
  setReverbWet,
  setVolume,
} from './audio/graph';
import { rewire } from './audio/effects-chain';
import {
  activePitches,
  applyEnvelopeSettings,
  installAudioUnlock,
  onPitchActive,
  panic,
  rebuildSource as rebuildAudioSource,
  releaseSustained,
} from './audio/engine';
import * as store from './state/store';
import { el, input, rafThrottle } from './ui/dom';
import { initControls, setEngineNotice } from './ui/controls';
import { createEffectsUi } from './ui/effects-ui';
import { createExtraParamsUi } from './ui/extra-params';
import { isCompactLayout, renderKeyboard, restoreActive, setKeyActive } from './ui/keyboard';
import { installInput, shiftMapping } from './ui/input';
import { installKeyboardResize } from './ui/resize';
import { installCollapsibles } from './ui/collapsible';
import { installKeyboardGate } from './ui/loading';
import { installOfflineUi } from './ui/offline';
import { installInstallButton } from './ui/install';
import { LANGS, applyDom, getLang, langName, setLang, synthLabel, t } from './i18n';
import type { Lang } from './i18n';

// Fill static translatable text before anything reads the DOM.
applyDom();

// Language selector in the header — switching persists and reloads.
const langSelect = el('langSelect') as HTMLSelectElement;
const sortedLangs = [...LANGS].sort((a, b) => langName(a).localeCompare(langName(b)));
for (const lang of sortedLangs) {
  const opt = document.createElement('option');
  opt.value = lang;
  opt.textContent = langName(lang);
  langSelect.appendChild(opt);
}
langSelect.value = getLang();
langSelect.addEventListener('change', () => setLang(langSelect.value as Lang));

const pianoEl = el('piano');
const scrollEl = el('pianoScroll');

// When keys get too narrow the note-name/octave labels are hidden, so their
// toggles are disabled (the computer-key label survives, so its toggle stays).
const labelToggleIds = ['showLetters', 'showSolfege', 'showOctave'] as const;
function syncLabelTogglesDisabled(): void {
  const compact = isCompactLayout();
  for (const id of labelToggleIds) {
    const box = input(id);
    box.disabled = compact;
    box.closest('.toggle-field')?.classList.toggle('disabled', compact);
  }
}

const drawKeyboard = (): void => {
  renderKeyboard(pianoEl, scrollEl, store.getSettings());
  // A re-render discards the elements the highlighting lived on.
  restoreActive(activePitches());
  syncLabelTogglesDisabled();
};
const requestKeyboardRender = rafThrottle(drawKeyboard);

// The engine owns which pitches are sounding, so highlighting stays correct
// when two input sources hold the same key.
onPitchActive(setKeyActive);

const extraParams = createExtraParamsUi(el('synthExtraParams'));
const effectsUi = createEffectsUi(el('effectsContainer'), setEngineNotice);

function rebuildSource(): void {
  const result = rebuildAudioSource(store.getSettings());
  if (!result.fallbackFrom) {
    setEngineNotice('');
    return;
  }
  setEngineNotice(
    result.reason === 'worklet'
      ? t('worklet.hint')
      : t('engine.fallback', { label: synthLabel(result.fallbackFrom), reason: String(result.reason) }),
    'err',
  );
}

const controls = initControls({
  requestKeyboardRender,
  rebuildSource,
  applySound: () => applyEnvelopeSettings(store.getSettings()),
  renderExtraParams: () => extraParams.render(),
  panic: () => panic(store.getSettings()),
  onReset: () => {
    ensureGraph();
    const s = store.getSettings();
    setVolume(s.volume);
    setFilterFrequency(s.filter);
    setEchoWet(s.echo);
    setReverbWet(s.reverb);
    rewire(s.effects);
    effectsUi.render();
    extraParams.render();
    rebuildSource();
    drawKeyboard();
  },
});

// The sustain checkbox is bound declaratively, but releasing the pedal has to
// flush the notes the engine is holding on its behalf.
input('sustain').addEventListener('change', (e) => {
  if (!(e.target as HTMLInputElement).checked) releaseSustained(store.getSettings());
});

installAudioUnlock();
installInput(pianoEl);
installCollapsibles(document.querySelector('.panel') ?? document);
installOfflineUi();
installInstallButton();

// Header buttons mirror the − = _ + mapping-shift shortcuts.
const shiftControls: ReadonlyArray<readonly [string, number, number]> = [
  ['shiftOctDown', -1, 0],
  ['shiftNoteDown', 0, -1],
  ['shiftNoteUp', 0, 1],
  ['shiftOctUp', 1, 0],
];
for (const [id, octaveDir, whiteDir] of shiftControls) {
  el(id).addEventListener('click', () => shiftMapping(octaveDir, whiteDir));
}
installKeyboardResize(el('keyboardResize'));
installKeyboardGate(
  document.querySelector('.keyboard-area') as HTMLElement,
  el('keyboardOverlay'),
  el('keyboardOverlayText'),
);
drawKeyboard();

// If the last session left us on a sampled instrument, load it up front so the
// keyboard can enable the moment it is ready, instead of on the first (silent)
// key-press.
if (store.get('engine') === 'sampler') rebuildSource();

store.on(
  ['rowShift', 'startAbs', 'endAbs', 'showLetters', 'showSolfege', 'showKeys', 'showOctave', 'displayMode', 'keyboardHeight'],
  requestKeyboardRender,
);
store.on(['engine'], () => controls.syncFromState());

let resizeTimer: number | null = null;
window.addEventListener('resize', () => {
  // Key height is a share of the viewport in every mode now, not just the
  // width-fitting ones, so every resize needs a re-layout.
  if (resizeTimer !== null) clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(drawKeyboard, 150);
});

if (WORKLETS_BLOCKED) console.warn(WORKLET_HINT);

if (import.meta.env.DEV) {
  void import('./test-hooks').then((m) => m.install());
}
